/**
 * Anticipatory time formatting — pairs with iOS Date+RelativeOrbit.swift.
 *
 * Studios feel a calendar of upcoming moments differently than a list
 * of dates. "Brennan Wedding · in 12 days" reads warmer than
 * "Brennan Wedding · June 14." Both surfaces should be reachable —
 * `relativeOrbit` picks anticipatory language by default; pair with
 * the absolute date in callers where long-term context matters.
 *
 * Used by:
 *   - RecentProjects card on dashboard
 *   - ProjectWorkspace right sidebar (due date)
 *   - Calendar event chips
 *
 * Output style — lowercase, sentence-case, never SHOUTY:
 *   "today" / "tomorrow" / "yesterday"
 *   "today · 3pm" (when includeTime)
 *   "in 3 days" / "5 days ago"
 *   "in 6 weeks"
 *   "in 4 months"
 *   "right now"
 */

const MS_PER_MIN = 60_000;
const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 86_400_000;

const formatTime = (d: Date) =>
  d
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    .toLowerCase()
    .replace(/\s/g, '');

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const startOfDay = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

export interface RelativeOptions {
  /** Append "· 3pm" when the date falls today/tomorrow/yesterday. */
  includeTime?: boolean;
  /** Override "now" for testing or scheduled-render contexts. */
  now?: Date;
}

/**
 * Pick anticipatory language for `target` relative to `now`.
 * See file header for output examples.
 */
export const relativeOrbit = (
  target: Date | string,
  opts: RelativeOptions = {},
): string => {
  const now = opts.now ?? new Date();
  const t = typeof target === 'string' ? new Date(target) : target;
  if (Number.isNaN(t.getTime())) return '';

  const future = t.getTime() >= now.getTime();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);

  // Same-day handling first — most common case.
  if (sameDay(t, now)) {
    if (opts.includeTime) {
      const ts = formatTime(t);
      return future ? `today · ${ts}` : `earlier today · ${ts}`;
    }
    const secondsAway = Math.abs(t.getTime() - now.getTime());
    if (secondsAway < MS_PER_HOUR) {
      const mins = Math.floor(secondsAway / MS_PER_MIN);
      if (mins < 5) return 'right now';
      return future ? `in ${mins} min` : `${mins} min ago`;
    }
    return future ? 'today' : 'earlier today';
  }

  if (sameDay(t, tomorrow)) {
    return opts.includeTime ? `tomorrow · ${formatTime(t)}` : 'tomorrow';
  }
  if (sameDay(t, yesterday)) {
    return 'yesterday';
  }

  const days = Math.round(
    (startOfDay(t) - startOfDay(now)) / MS_PER_DAY,
  );
  const absDays = Math.abs(days);

  if (absDays <= 30) {
    return future ? `in ${absDays} days` : `${absDays} days ago`;
  }

  const weeks = Math.floor(absDays / 7);
  if (weeks <= 12) {
    const unit = weeks === 1 ? 'week' : 'weeks';
    return future ? `in ${weeks} ${unit}` : `${weeks} ${unit} ago`;
  }

  const months = Math.max(1, Math.floor(absDays / 30));
  const unit = months === 1 ? 'month' : 'months';
  return future ? `in ${months} ${unit}` : `${months} ${unit} ago`;
};

/**
 * Compact due-date label — pairs the relative phrasing with "Due"
 * prefix or "overdue" suffix for project workspace use.
 *   "Due in 3 days" / "Due tomorrow" / "5 days overdue"
 */
export const dueLabelOrbit = (
  target: Date | string,
  now: Date = new Date(),
): string => {
  const label = relativeOrbit(target, { now });
  if (!label) return '';
  if (label.startsWith('in ')) return `Due ${label}`;
  if (label.endsWith(' ago')) return label.replace(' ago', ' overdue');
  if (label === 'today') return 'Due today';
  if (label === 'tomorrow') return 'Due tomorrow';
  if (label === 'yesterday') return 'Due yesterday';
  return `Due ${label}`;
};
